<template>
  <div class="desktop">
    <div class="hero-section-parent">
      <HeroSection />
      <ContentSection
        :heading="t('page.section1_heading')"
        imageSrc="/sad-kid.webp"
        :imageAlt="t('page.section1_alt')"
        imagePosition="right"
      >
        <div class="quotes-section">
          <p class="truth-text" v-html="t('page.parents_quotes_heading')"></p>
          <p class="truth-text" v-html="t('page.quote1')"></p>
          <p class="truth-text" v-html="t('page.quote2')"></p>
          <p class="truth-text" v-html="t('page.quote3')"></p>
          <p class="truth-text" v-html="t('page.quote4')"></p>
        </div>

        <div class="truth-section">
          <p class="truth-text">{{ t('page.truth_text') }}</p>
          <p class="truth-text">{{ t('page.problem1') }}</p>
          <p class="truth-text">{{ t('page.problem2') }}</p>
          <p class="truth-text">{{ t('page.problem3') }}</p>
          <p class="truth-text">{{ t('page.problem4') }}</p>
        </div>

        <div class="result-section">
          <p class="truth-text">{{ t('page.result1') }}</p>
          <p class="truth-text">{{ t('page.result2') }}</p>
        </div>

        <p class="truth-text">{{ t('page.seo_text') }}</p>
      </ContentSection>

      <ContentSection
        :heading="t('page.section2_heading')"
        :subheading="t('page.section2_subheading')"
        videoUrl="https://www.youtube.com/embed/vR9SfFimwC0?si=kce9esycHz6HJCE1"
        videoThumbnail="/vidThump.png"
        imagePosition="left"
        :show-button="true"
        secret
        @buttonClick="handleBookTrial"
      >
        <div class="immersion-content">
          <div class="info-box">
            <p class="truth-text">{{ t('page.immersion_text1') }}</p>
            <p class="truth-text">{{ t('page.immersion_text2') }}</p>
            <p class="truth-text">{{ t('page.immersion_text3') }}</p>
          </div>
        </div>
      </ContentSection>
    </div>

    <TestimonialsSection />

    <ContentSection
      :heading="t('page.section3_heading')"
      imageSrc="/happy-kids.webp"
      :imageAlt="t('page.section3_alt')"
      imagePosition="right"
      :show-button="true"
      @buttonClick="handleBookTrial"
    >
      <div class="mother-tongue-content">
        <p class="truth-text">{{ t('page.mother_tongue_text1') }}</p>
        <p class="truth-text">{{ t('page.mother_tongue_text2') }}</p>
        <p class="truth-text">{{ t('page.mother_tongue_text3') }}</p>
        <p class="truth-text">{{ t('page.mother_tongue_text4') }}</p>
        <p class="truth-text">{{ t('page.mother_tongue_text5') }}</p>
        <p class="truth-text">{{ t('page.mother_tongue_text6') }}</p>
      </div>
    </ContentSection>

    <ImmersionMethodology />

    <VideoCarousel :items="videoReviewsData" />

     <ClubsSection
      :clubs="clubsData"
      @cta-click="handleBookTrial"
    />

    <ContentSection
      :heading="t('page.section5_heading')"
      imageSrc="/founder.png"
      :imageAlt="t('page.section5_alt')"
      imagePosition="left"
    >
      <div class="custom-content">
        <p class="truth-text">{{ t('page.trust_text1') }}</p>
        <p class="truth-text">{{ t('page.trust_text2') }}</p>
        <p class="truth-text">{{ t('page.trust_text3') }}</p>
        <p class="truth-text">{{ t('page.trust_text4') }}</p>
        <p class="truth-text">{{ t('page.trust_text5') }}</p>
        <p class="truth-text">{{ t('page.trust_text6') }}</p>
        <p class="truth-text">{{ t('page.trust_text7') }}</p>
        <p class="truth-text">{{ t('page.trust_text8') }}</p>
        <p class="truth-text">{{ t('page.trust_text9') }}</p>
        <p class="truth-text">{{ t('page.trust_text10') }}</p>
        <p class="truth-text">{{ t('page.trust_text11') }}</p>
      </div>
    </ContentSection>

    <StudentTestimonials
      :testimonials="studentTestimonialsData"
      @videoPlay="handleVideoPlay"
      @ctaClick="handleBookTrial"
    />

    <StatsSection />

    <ContentSection
      :heading="t('page.section7_heading')"
    />

    <AccreditationSection />

    <PricingSection @ctaClick="handleBookTrial" />

    <ContentSection
      :heading="t('page.section8_heading')"
      :subheading="t('page.section8_subheading')"
      imageSrc="/six-faster.webp"
      :imageAlt="t('page.section8_alt')"
      imagePosition="left"
      :show-button="true"
      @buttonClick="handleBookTrial"
    >
      <div class="trial-section-content">
        <p class="truth-text">{{ t('page.trial_text1') }}</p>
        <p class="truth-text">{{ t('page.trial_text2') }}</p>
      </div>
    </ContentSection>

    <FAQSection @ctaClick="handleBookTrial" />

    <!-- Working Footer - No i18n dependency -->
    <WorkingFooter />

  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import HeroSection from './sections/HeroSection.vue';
import ContentSection from './ui/ContentSection.vue';
import TestimonialsSection from './sections/TestimonialsSection.vue';
import ImmersionMethodology from './sections/ImmersionMethodology.vue';
import ClubsSection from './sections/ClubsSection.vue';
import VideoCarousel from './ui/VideoCarousel.vue';
import StudentTestimonials from './sections/StudentTestimonials.vue';
import StatsSection from './sections/StatsSection.vue';
import AccreditationSection from './sections/AccreditationSection.vue';
import PricingSection from './sections/PricingSection.vue';
import FAQSection from './sections/FAQSection.vue';
import WorkingFooter from './sections/WorkingFooter.vue';

// Import data from JSON files
import videoReviewsDataRaw from '@/constants/videoReviews.json';
import clubsDataRaw from '@/constants/clubs.json';
import studentTestimonialsDataRaw from '@/constants/studentTestimonials.json';

const { t, isRTL, initializeLanguage } = useLanguage()

// Initialize language on component mount
onMounted(() => {
  initializeLanguage()
})

// Merge video reviews data with translations
const videoReviewsData = computed(() => {
  return videoReviewsDataRaw.map((video, index) => ({
    thumbnailUrl: video.thumbnailUrl,
    videoUrl: video.videoUrl,
    title: t(`video_reviews.items.${index}.title`),
    description: t(`video_reviews.items.${index}.description`),
    alt: t(`video_reviews.items.${index}.alt`)
  }))
})

// Merge clubs data with translations
const clubsData = computed(() => {
  return clubsDataRaw.map((club, index) => {
    // Get the number of tags from the original data
    const translatedTags = club.tags.map((_, tagIndex) =>
      t(`clubs_data.items.${index}.tags.${tagIndex}`)
    )

    return {
      id: club.id,
      name: t(`clubs_data.items.${index}.name`),
      description: t(`clubs_data.items.${index}.description`),
      students: club.students,
      rating: club.rating,
      tags: translatedTags,
      image: isRTL.value ? club.imageAr : club.image
    }
  })
})

// Merge student testimonials data with translations
const studentTestimonialsData = computed(() => {
  return studentTestimonialsDataRaw.map(testimonial => ({
    id: testimonial.id,
    textKey: testimonial.textKey,
    videoUrl: testimonial.videoUrl
  }))
})

const handleBookTrial = () => {
  window.open('https://monglish.zohobookings.com/#/4757440000000046052', '_blank');
}

const handleVideoPlay = (videoUrl) => {
  // Add your video play logic here
  console.log('Playing video:', videoUrl);
}
</script>

<style scoped>
.desktop {
  width: 100%;
  max-width: 100vw;
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  text-align: center;
  font-size: var(--text-base);
  color: var(--color-text);
  font-family: 'Poppins', sans-serif;
}

/* RTL specific - text direction */
.desktop[dir="rtl"] {
  text-align: right;
}

/* Arabic typography - Cairo */
.desktop[dir="rtl"],
.desktop[dir="rtl"] * {
  font-family: 'Cairo', 'Arial', sans-serif;
}

/* Decorative background circles - responsive */
.desktop-child,
.desktop-item,
.desktop-inner {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(22, 94, 132, 0.15);
  box-sizing: border-box;
  pointer-events: none;
  display: none;
}

.ellipse-div,
.desktop-child2 {
  position: fixed;
  filter: blur(300px);
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  opacity: 0.15;
  width: clamp(200px, 30vw, 413px);
  height: clamp(220px, 33vw, 446px);
  pointer-events: none;
  z-index: 0;
}

.ellipse-div {
  bottom: 20%;
  left: -150px;
}

.desktop-child2 {
  bottom: 30%;
  right: -150px;
}

.frame-icon,
.mask-group-icon {
  display: none;
}

.hero-section-parent {
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: var(--spacing-2xl);
}

.frame-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: clamp(20px, 4vw, 60px) clamp(15px, 3vw, 40px);
  gap: clamp(30px, 5vw, 60px);
  position: relative;
}

/* RTL specific - Reduced padding and gap */
html[lang="ar"] .frame-group {
  padding: clamp(15px, 2.5vw, 35px) clamp(10px, 2vw, 25px);
  gap: clamp(15px, 2.5vw, 30px);
}

.frame-container {
  display: none;
}

.frame-item,
.frame-inner,
.frame-child2,
.group-div,
.your-child-deserves-container,
.ellipse-parent,
.frame-child6,
.frame-child7 {
  display: none;
}

.join {
  margin: 0;
}

.excellence {
  color: var(--color-primary);
}

/* RTL specific - Reduced spacing for text sections */
html[lang="ar"] .quotes-section {
  margin: clamp(4px, 0.8vw, 6px) 0;
}

html[lang="ar"] .truth-section {
  margin: clamp(4px, 0.8vw, 6px) 0;
}

html[lang="ar"] .result-section {
  margin: clamp(4px, 0.8vw, 6px) 0;
}

html[lang="ar"] .quote {
  margin: clamp(2px, 0.4vw, 3px) 0;
  line-height: 1.4;
}

html[lang="ar"] .truth-text {
  line-height: 1.5;
}

html[lang="ar"] .result-text {
  margin: clamp(2px, 0.4vw, 3px) 0;
  line-height: 1.5;
}

html[lang="ar"] .info-text {
  margin: clamp(3px, 0.6vw, 4px) 0;
  line-height: 1.6;
}

html[lang="ar"] .trial-text {
  margin: clamp(3px, 0.6vw, 4px) 0;
  line-height: 1.6;
}

html[lang="ar"] .coach-text {
  margin: clamp(4px, 0.7vw, 5px) 0;
  line-height: 1.5;
}

html[lang="ar"] .coach-answer {
  margin: clamp(5px, 0.8vw, 6px) 0;
  line-height: 1.5;
}

/* Responsive adjustments */
@media (max-width: 1400px) {
  .hero-section-parent {
    padding: 0 clamp(20px, 4vw, 60px);
  }

  html[lang="ar"] .hero-section-parent {
    padding: 0 clamp(15px, 3vw, 40px);
  }
}

@media (max-width: 1024px) {
  .ellipse-div {
    left: -100px;
    filter: blur(200px);
  }

  .desktop-child2 {
    right: -100px;
    filter: blur(200px);
  }
}

@media (max-width: 768px) {
  .desktop {
    font-size: 14px;
  }

  .frame-group {
    gap: clamp(25px, 4vw, 40px);
  }

  .ellipse-div,
  .desktop-child2 {
    filter: blur(150px);
    opacity: 0.1;
  }

  .quote,
  .result-text,
  .info-text,
  .trial-text,
  .problems-list li {
    font-size: 15px;
  }

  .truth-text {
    font-size: 17px;
  }

  .coach-text {
    font-size: 18px;
  }

  .coach-answer {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .hero-section-parent {
    padding: 0 15px;
  }

  .frame-group {
    padding: 20px 10px;
    gap: 25px;
  }

  .ellipse-div {
    left: -80px;
  }

  .desktop-child2 {
    right: -80px;
  }
}

/* ContentSection custom styles */
.quotes-section {
  margin: var(--spacing-sm) 0;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .quotes-section {
  text-align: right;
  direction: rtl;
}

.quote {
  font-size: var(--text-base);
  line-height: 1.6;
  margin: var(--spacing-xs) 0;
  color: var(--color-text);
  font-style: italic;
  text-align: inherit;
  width: 100%;
}

/* Arabic quote styles - H2 */
.desktop[dir="rtl"] .quote {
  font-size: var(--text-xl);
  font-weight: 700;
  line-height: 1.5;
  font-style: normal;
}

.truth-section {
  margin: var(--spacing-sm) 0;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .truth-section {
  text-align: right;
  direction: rtl;
}

.truth-text {
  font-size: var(--text-lg);
  font-weight: 600;
  margin: var(--spacing-xs) 0;
  color: var(--color-title);
  text-align: inherit;
  width: 100%;
  line-height: 1.6;
}

/* Arabic truth-text - H5 */
.desktop[dir="rtl"] .truth-text {
  font-size: var(--text-base);
  font-weight: 500;
  line-height: 1.7;
}

.problems-list {
  list-style: none;
  padding: 0;
  margin: clamp(4px, 0.8vw, 6px) 0;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .problems-list {
  text-align: right;
}

.problems-list li {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin: clamp(3px, 0.5vw, 4px) 0;
  text-align: inherit;
  width: 100%;
}

/* Arabic list items - H5 */
.desktop[dir="rtl"] .problems-list li {
  font-size: clamp(14px, 2vw, 18px);
  font-weight: 400;
  line-height: 1.8;
}

.result-section {
  margin: var(--spacing-sm) 0;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .result-section {
  text-align: right;
  direction: rtl;
}

.result-text {
  font-size: var(--text-base);
  line-height: 1.6;
  margin: var(--spacing-xs) 0;
  color: var(--color-text);
  font-weight: 500;
  text-align: inherit;
  width: 100%;
}

/* Arabic result text - P1 */
.desktop[dir="rtl"] .result-text {
  font-size: var(--text-base);
  font-weight: 400;
  line-height: 1.7;
}

.seo-text {
  font-size: 12px;
  color: #666;
  margin: clamp(10px, 2vw, 15px) 0 0 0;
  font-style: italic;
  opacity: 0.8;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .seo-text {
  text-align: right;
}

/* Arabic seo-text - P2 */
.desktop[dir="rtl"] .seo-text {
  font-size: clamp(11px, 1.3vw, 14px);
  font-weight: 400;
  line-height: 1.6;
  font-style: normal;
}

/* Immersion section styles */
.immersion-content,
.mother-tongue-content,
.swimming-coach-content,
.trial-section-content,
.custom-content,
.registration-content {
  width: 100%;
  text-align: inherit;
}

.desktop[dir="rtl"] .immersion-content,
.desktop[dir="rtl"] .mother-tongue-content,
.desktop[dir="rtl"] .swimming-coach-content,
.desktop[dir="rtl"] .trial-section-content,
.desktop[dir="rtl"] .custom-content,
.desktop[dir="rtl"] .registration-content {
  text-align: right;
  direction: rtl;
}

.info-text {
  font-size: var(--text-base);
  line-height: 1.7;
  margin: var(--spacing-xs) 0;
  color: var(--color-text);
  text-align: inherit;
  width: 100%;
}

/* Arabic info-text - H5 */
.desktop[dir="rtl"] .info-text {
  font-size: var(--text-base);
  font-weight: 400;
  line-height: 1.8;
}

.coach-text {
  font-size: 20px;
  line-height: 1.7;
  margin: clamp(8px, 1.2vw, 10px) 0;
  color: #333;
  font-weight: 500;
  text-align: inherit;
  width: 100%;
}

/* Arabic coach-text - H3 */
.desktop[dir="rtl"] .coach-text {
  font-size: clamp(18px, 2.5vw, 24px);
  font-weight: 600;
  line-height: 1.6;
  text-align: right;
}

.coach-answer {
  font-size: 24px;
  line-height: 1.5;
  margin: clamp(10px, 1.5vw, 12px) 0;
  color: #000;
  font-weight: 700;
  text-align: inherit;
  width: 100%;
}

/* Arabic coach-answer - H4 */
.desktop[dir="rtl"] .coach-answer {
  font-size: clamp(16px, 2.2vw, 22px);
  font-weight: 600;
  line-height: 1.6;
  text-align: right;
}

.trial-text {
  font-size: 16px;
  line-height: 1.7;
  margin: clamp(6px, 1vw, 8px) 0;
  color: #333;
  text-align: inherit;
  width: 100%;
}

.desktop[dir="rtl"] .trial-text {
  text-align: right;
}

/* Arabic trial-text - H5 */
.desktop[dir="rtl"] .trial-text {
  font-size: clamp(14px, 2vw, 18px);
  font-weight: 400;
  line-height: 1.8;
}

/* Additional mobile optimizations */
@media (max-width: 640px) {
  .quotes-section,
  .truth-section,
  .result-section {
    margin: 8px 0;
  }

  .problems-list li {
    line-height: 1.6;
  }
}

@media (max-width: 380px) {
  .quote,
  .result-text,
  .info-text,
  .trial-text,
  .problems-list li {
    font-size: 14px;
  }

  .truth-text {
    font-size: 16px;
  }

  .coach-text {
    font-size: 16px;
  }

  .coach-answer {
    font-size: 18px;
  }
}
</style>
